import React, { useEffect, useState } from "react";
import {
  Build,
  FlashOn,
  Search,
  Security,
  TrendingDown,
  Visibility,
  Warning,
  CheckCircle,
  EmojiObjects,
} from "@mui/icons-material";
import { DiagnosticResult } from "./DiagnosticQuiz";
import { DiagnosticScores } from "./questions";

interface DiagnosticResultsProps {
  results: DiagnosticResult[];
}

type ObstacleType = "vision" | "execution" | "sustainment";

interface ObstacleDetails {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  overview: string[];
  symptoms: string[];
  causes: string[];
  effects: string[];
  immediateActions: string[];
  goodNews: string;
  nextStep: string;
  closingStatement: string;
}

// Max possible scores per category (sum of max option score per question)
const MAX_SCORES: DiagnosticScores = {
  vision: 20,
  execution: 20,
  sustainment: 22,
};

const DiagnosticResults: React.FC<DiagnosticResultsProps> = ({ results }) => {
  const [animationStep, setAnimationStep] = useState(0);
  const [barsVisible, setBarsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const timer = setInterval(() => {
      setAnimationStep((prev) => Math.min(prev + 1, 3));
    }, 300);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (animationStep >= 2) {
      const t = setTimeout(() => setBarsVisible(true), 100);
      return () => clearTimeout(t);
    }
  }, [animationStep]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const calculateTotalScores = (): DiagnosticScores => {
    return results.reduce(
      (totals, result) => ({
        vision: totals.vision + result.scores.vision,
        execution: totals.execution + result.scores.execution,
        sustainment: totals.sustainment + result.scores.sustainment,
      }),
      { vision: 0, execution: 0, sustainment: 0 },
    );
  };

  const determinePrimaryObstacle = (): ObstacleType => {
    if (results.length === 0) return "vision";

    const totals = calculateTotalScores();
    const maxScore = Math.max(
      totals.vision,
      totals.execution,
      totals.sustainment,
    );

    const visionDiff =
      totals.vision - Math.max(totals.execution, totals.sustainment);
    const executionDiff =
      totals.execution - Math.max(totals.vision, totals.sustainment);
    const sustainmentDiff =
      totals.sustainment - Math.max(totals.vision, totals.execution);

    if (visionDiff >= 2) return "vision";
    if (executionDiff >= 2) return "execution";
    if (sustainmentDiff >= 2) return "sustainment";

    if (totals.vision === maxScore) return "vision";
    if (totals.execution === maxScore) return "execution";
    return "sustainment";
  };

  const obstacleDetailsMap: Record<ObstacleType, ObstacleDetails> = {
    vision: {
      title: "Your Primary Gap: Clarity",
      subtitle:
        "Your business is moving — but not everyone is moving in the same direction.",
      icon: <Visibility style={{ fontSize: "3rem" }} />,
      color: "#c9a750",
      bgColor: "#fefce8",
      overview: [
        "This isn't a motivation problem. It's a structural one. When leadership doesn't share a single defined vision, every decision below it gets filtered through someone's personal interpretation.",
        "You're not dealing with a people problem. You're dealing with a clarity problem — and until it's resolved, every initiative you launch will compete with a different version of the strategy.",
      ],
      symptoms: [
        "Strategic priorities shift depending on who is in the room, with no consistent position from leadership.",
        "Initiatives launch without full buy-in from the full leadership team behind them.",
        "Teams work hard but don't feel like they're making meaningful progress toward a shared goal.",
        "Decisions get interpreted differently at every level of the organization.",
      ],
      causes: [
        "Your leadership team hasn't locked in a single, shared definition of what success looks like.",
        "The vision is discussed but never formally committed to in a way the organization can act on.",
        "Each leader interprets strategy through their own department's priorities and lens.",
        "There's no agreed language across the organization for what winning actually means.",
      ],
      effects: [
        "Every month without a locked vision is a month your competitors move faster with clearer focus.",
        "Resources get allocated to competing interpretations of the same strategy simultaneously.",
        "Teams lose confidence in leadership when direction keeps shifting without explanation.",
        "New initiatives stall before they gain traction because there's no stable foundation to build on.",
      ],
      immediateActions: [
        "Get your leadership team in a room and surface the top three areas of active disagreement.",
        "Schedule a structured alignment session with your full leadership team within the next 30 days.",
        "Pause new initiative launches until you have a locked, shared direction everyone can repeat.",
        "Write one sentence that defines success — one that every leader can articulate without hesitation.",
      ],
      goodNews:
        "The good news: this is one of the most fixable problems in business — when you approach it the right way.",
      nextStep:
        "Your next step is a 60-minute MAP Session with Sabrish Chand. Use the calendar below to book your session.",
      closingStatement:
        "In a focused 60-minute session, we'll identify exactly where the misalignment is coming from and map a path to get your leadership team pointing in one direction.",
    },
    execution: {
      title: "Your Primary Gap: Systems",
      subtitle:
        "You know where you want to go. The problem is your systems aren't getting you there.",
      icon: <Build style={{ fontSize: "3rem" }} />,
      color: "#c9a750",
      bgColor: "#fefce8",
      overview: [
        "Somewhere between strategy and delivery, things break down. Initiatives stall. Momentum fades. The gap between what leadership planned and what actually happened keeps widening.",
        "This isn't a vision problem. The direction is clear. What's missing is the operating structure that turns decisions into results — reliably, repeatedly, and without depending on the same few people to hold it together.",
      ],
      symptoms: [
        "Projects start with energy but lose traction before they reach completion.",
        "Teams aren't sure who owns what or who has final decision-making authority.",
        "Leadership spends more time managing problems than driving the business forward.",
        "The gap between what was planned and what got delivered keeps widening with each initiative.",
      ],
      causes: [
        "There are no clear accountability structures linking strategic decisions to delivery outcomes.",
        "No reliable system tracks work from the moment a commitment is made through to completion.",
        "Communication breaks down between the people setting strategy and those responsible for executing it.",
        "Too many competing priorities pull from the same limited pool of people and resources at once.",
      ],
      effects: [
        "Every stalled initiative is an opportunity your competition picks up and runs with while you're still trying to move.",
        "Your strongest performers burn out investing significant effort into work that never lands.",
        "The gap between your stated strategy and your actual results becomes visible to clients, partners, and investors.",
        "Missed delivery compounds — each gap makes the next one harder to close and harder to justify.",
      ],
      immediateActions: [
        "Identify your three most stalled initiatives and pinpoint exactly where each one broke down.",
        "Assign a single accountable owner to each active project — one specific person, not a team or committee.",
        "Set up a weekly execution review to surface and clear blockers before they compound into delays.",
        "Put your lowest-priority work on hold so your team can go deep on the work that actually moves the business.",
      ],
      goodNews:
        "You don't need more planning. You need a clearer path from decision to result.",
      nextStep:
        "Your next step is a 60-minute MAP Session with Sabrish Chand. Use the calendar below to book your session.",
      closingStatement:
        "In a focused 60-minute session, we'll pinpoint where your systems are breaking down and identify the highest-leverage fix.",
    },
    sustainment: {
      title: "Your Primary Gap: Execution",
      subtitle:
        "Your organization can implement change. The problem is making it stick.",
      icon: <Security style={{ fontSize: "3rem" }} />,
      color: "#c9a750",
      bgColor: "#fefce8",
      overview: [
        "You've invested in new systems, new processes, maybe new teams. And for a while, things improve. But six months later you're back where you started. Old habits return. Adoption falls off.",
        "The change that looked like a success on paper quietly dissolves back into how things were — not because your people don't care, but because the structure wasn't built to hold it.",
      ],
      symptoms: [
        "New technology gets adopted at launch and quietly abandoned within months.",
        "Process improvements don't survive leadership transitions or organizational restructuring.",
        "Adoption is inconsistent — some teams use it fully, others never did.",
        "Your organization has had to re-launch the same initiative more than once to achieve basic compliance.",
      ],
      causes: [
        "Change is being treated as a one-time event rather than an ongoing system that requires reinforcement.",
        "There's no structured reinforcement plan built into new initiatives at the point of design.",
        "Leadership moves attention to the next priority before confirming adoption has actually taken hold.",
        "Success is measured at launch — not at 90 or 180 days when it either proves out or falls apart.",
      ],
      effects: [
        "Every failed execution cycle costs you twice — once for the investment and once for the loss of organizational credibility.",
        "When teams repeatedly see changes that don't stick, they stop believing the next one will be any different.",
        "Repeated re-launches drain budget, erode team morale, and consume leadership bandwidth that should be driving growth.",
        "Organizational skepticism compounds and becomes the hardest barrier to overcome in any future change effort.",
      ],
      immediateActions: [
        "Review your last three major change initiatives and identify exactly where adoption broke down in each one.",
        "Define what a sustained change looks like for your organization — in measurable, time-bound terms.",
        "Build a structured 90-day reinforcement plan into every future initiative before it launches, not as an afterthought.",
        "Identify the one change most at risk of reverting right now and take immediate steps to stabilize it.",
      ],
      goodNews:
        "You've already proven you can start. Now let's build something that lasts.",
      nextStep:
        "Your next step is a 60-minute MAP Session with Sabrish Chand. Use the calendar below to book your session.",
      closingStatement:
        "In a focused 60-minute session, we'll identify why change isn't sticking and design an approach that removes the reversion risk.",
    },
  };

  const totals = calculateTotalScores();
  const primaryObstacle = determinePrimaryObstacle();
  const obstacleDetails = obstacleDetailsMap[primaryObstacle];

  // Bar widths: normalize relative to max possible score per category
  const getBarWidth = (score: number, type: keyof DiagnosticScores) => {
    const pct = Math.round((score / MAX_SCORES[type]) * 100);
    return Math.min(pct, 100);
  };

  const getRiskLabel = (score: number, type: keyof DiagnosticScores) => {
    const pct = (score / MAX_SCORES[type]) * 100;
    if (pct >= 55) return { label: "High Risk", color: "#ef4444" };
    if (pct >= 30) return { label: "Moderate", color: "#f59e0b" };
    return { label: "Low Risk", color: "#10b981" };
  };

  const scoreCards = [
    { type: "vision" as const, label: "Clarity", icon: <Visibility /> },
    { type: "execution" as const, label: "Systems", icon: <Build /> },
    { type: "sustainment" as const, label: "Execution", icon: <Security /> },
  ];

  const infoSections = [
    {
      key: "symptoms",
      title: "Symptoms",
      icon: <Warning style={{ color: "white", fontSize: "1.1rem" }} />,
      headerBg: "#f59e0b",
      bodyBg: "#fffbeb",
      borderColor: "#fde68a",
      items: obstacleDetails.symptoms,
      numberColor: "#f59e0b",
    },
    {
      key: "causes",
      title: "Root Causes",
      icon: <Search style={{ color: "white", fontSize: "1.1rem" }} />,
      headerBg: "#3b82f6",
      bodyBg: "#eff6ff",
      borderColor: "#bfdbfe",
      items: obstacleDetails.causes,
      numberColor: "#3b82f6",
    },
    {
      key: "effects",
      title: "If Left Unresolved",
      icon: <TrendingDown style={{ color: "white", fontSize: "1.1rem" }} />,
      headerBg: "#ef4444",
      bodyBg: "#fef2f2",
      borderColor: "#fecaca",
      items: obstacleDetails.effects,
      numberColor: "#ef4444",
    },
    {
      key: "actions",
      title: "Immediate Actions",
      icon: <FlashOn style={{ color: "white", fontSize: "1.1rem" }} />,
      headerBg: "#10b981",
      bodyBg: "#ecfdf5",
      borderColor: "#a7f3d0",
      items: obstacleDetails.immediateActions,
      numberColor: "#10b981",
    },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #152A53 100%)",
        padding: isMobile ? "2rem 1rem" : "3rem 2rem",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative bg elements */}
      <div
        style={{
          position: "absolute",
          top: "-150px",
          right: "-150px",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background: "rgba(201, 167, 80, 0.07)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-100px",
          left: "-100px",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.04)",
          pointerEvents: "none",
        }}
      />

      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        {/* Header */}
        <div
          style={{
            textAlign: "center",
            marginBottom: "2.5rem",
            opacity: animationStep >= 1 ? 1 : 0,
            transform:
              animationStep >= 1 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-out",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              backgroundColor: "rgba(201,167,80,0.2)",
              border: "1px solid rgba(201,167,80,0.4)",
              borderRadius: "20px",
              padding: "0.35rem 1rem",
              marginBottom: "1rem",
            }}
          >
            <CheckCircle style={{ color: "#c9a750", fontSize: "1rem" }} />
            <span
              style={{
                fontSize: "0.75rem",
                fontWeight: "700",
                color: "#e4c070",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Assessment Complete
            </span>
          </div>
          <h1
            style={{
              fontSize: isMobile ? "2rem" : "2.75rem",
              fontWeight: "800",
              color: "white",
              marginBottom: "0.75rem",
              lineHeight: "1.2",
            }}
          >
            Your Results Are In
          </h1>
          <p
            style={{
              fontSize: isMobile ? "1rem" : "1.25rem",
              color: "rgba(255,255,255,0.9)",
              maxWidth: "760px",
              margin: "0 auto",
              lineHeight: "1.5",
            }}
          >
            Based on your responses, we've identified your primary gap — and
            what to do about it.
          </p>
        </div>

        {/* Score Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
            gap: "1.25rem",
            marginBottom: "2.5rem",
            opacity: animationStep >= 2 ? 1 : 0,
            transform:
              animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-out 0.2s",
          }}
        >
          {scoreCards.map((item) => {
            const isPrimary = primaryObstacle === item.type;
            const score = totals[item.type];
            const barPct = getBarWidth(score, item.type);
            const risk = getRiskLabel(score, item.type);

            return (
              <div
                key={item.type}
                style={{
                  backgroundColor: isPrimary ? "#fefce8" : "white",
                  borderRadius: "16px",
                  padding: "1.75rem",
                  boxShadow: isPrimary
                    ? "0 12px 35px rgba(201, 167, 80, 0.3)"
                    : "0 8px 25px rgba(0, 0, 0, 0.15)",
                  border: isPrimary
                    ? "2px solid #c9a750"
                    : "2px solid transparent",
                  position: "relative",
                  transition: "transform 0.2s",
                }}
              >
                {isPrimary && (
                  <div
                    style={{
                      position: "absolute",
                      top: "-13px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      background: "linear-gradient(135deg, #c9a750, #b8923d)",
                      color: "white",
                      padding: "0.35rem 1rem",
                      borderRadius: "20px",
                      fontSize: "0.7rem",
                      fontWeight: "800",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      whiteSpace: "nowrap",
                      boxShadow: "0 3px 10px rgba(201,167,80,0.4)",
                    }}
                  >
                    Primary Obstacle
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "1.25rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.625rem",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "36px",
                        height: "36px",
                        borderRadius: "10px",
                        backgroundColor: isPrimary ? "#c9a750" : "#f1f5f9",
                        color: isPrimary ? "white" : "#64748b",
                      }}
                    >
                      {item.icon}
                    </div>
                    <h3
                      style={{
                        fontSize: "1rem",
                        fontWeight: "700",
                        color: "#1a202c",
                        margin: 0,
                      }}
                    >
                      {item.label}
                    </h3>
                  </div>
                  <span
                    style={{
                      fontSize: "0.7rem",
                      fontWeight: "700",
                      color: risk.color,
                      backgroundColor: `${risk.color}18`,
                      padding: "0.2rem 0.625rem",
                      borderRadius: "20px",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {risk.label}
                  </span>
                </div>

                {/* Score bar */}
                <div
                  style={{
                    width: "100%",
                    height: "8px",
                    backgroundColor: "#e2e8f0",
                    borderRadius: "4px",
                    overflow: "hidden",
                    marginBottom: "0.625rem",
                  }}
                >
                  <div
                    style={{
                      width: barsVisible ? `${barPct}%` : "0%",
                      height: "100%",
                      background: isPrimary
                        ? "linear-gradient(90deg, #c9a750, #e4c070)"
                        : "linear-gradient(90deg, #94a3b8, #cbd5e1)",
                      borderRadius: "4px",
                      transition: "width 0.9s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: "#a0aec0",
                    }}
                  >
                    Score: {score} pts
                  </span>
                  <span
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: "700",
                      color: isPrimary ? "#c9a750" : "#94a3b8",
                    }}
                  >
                    {barPct}% of max
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Main Detail Card */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "24px",
            padding: isMobile ? "2rem" : "3rem",
            marginBottom: "2rem",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            opacity: animationStep >= 3 ? 1 : 0,
            transform:
              animationStep >= 3 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-out 0.4s",
          }}
        >
          {/* Primary Obstacle Hero */}
          <div
            style={{
              textAlign: "center",
              marginBottom: "2.5rem",
              padding: isMobile ? "1.75rem" : "2.5rem",
              background: "linear-gradient(135deg, #fefce8 0%, #fffbeb 100%)",
              borderRadius: "20px",
              border: "1px solid #e4d6a7",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Decorative corner accent */}
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: "120px",
                height: "120px",
                background:
                  "radial-gradient(circle at top right, rgba(201,167,80,0.15), transparent)",
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "88px",
                height: "88px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #c9a750, #b8923d)",
                color: "white",
                marginBottom: "1.25rem",
                boxShadow: "0 12px 30px rgba(201, 167, 80, 0.45)",
              }}
            >
              {obstacleDetails.icon}
            </div>
            <h2
              style={{
                fontSize: isMobile ? "1.6rem" : "2.25rem",
                fontWeight: "800",
                color: "#152A53",
                marginBottom: "0.75rem",
                lineHeight: "1.2",
              }}
            >
              {obstacleDetails.title}
            </h2>
            <p
              style={{
                fontSize: isMobile ? "1rem" : "1.1rem",
                color: "#4a5568",
                fontWeight: "600",
                lineHeight: "1.6",
                margin: 0,
                maxWidth: "560px",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              {obstacleDetails.subtitle}
            </p>
          </div>

          {/* Overview */}
          <div
            style={{
              marginBottom: "2.5rem",
              borderLeft: "4px solid #c9a750",
              paddingLeft: "1.5rem",
            }}
          >
            {obstacleDetails.overview.map((paragraph, index) => (
              <p
                key={index}
                style={{
                  fontSize: "1.05rem",
                  lineHeight: "1.8",
                  color: "#2d3748",
                  marginBottom:
                    index === obstacleDetails.overview.length - 1 ? 0 : "1rem",
                  fontStyle: index === 0 ? "italic" : "normal",
                  fontWeight: index === 0 ? "600" : "400",
                }}
              >
                {paragraph}
              </p>
            ))}
          </div>

          {/* Info Sections Grid */}
          <div
            style={{
              backgroundColor: "#f8fafc",
              borderRadius: "16px",
              padding: isMobile ? "1.5rem" : "2rem",
              marginBottom: "2rem",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "1.25rem",
                marginBottom: "2rem",
              }}
            >
              {infoSections.map((section) => (
                <div
                  key={section.key}
                  style={{
                    borderRadius: "12px",
                    overflow: "hidden",
                    border: `1px solid ${section.borderColor}`,
                  }}
                >
                  <div
                    style={{
                      backgroundColor: section.headerBg,
                      padding: "0.75rem 1.25rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    {section.icon}
                    <h4
                      style={{
                        color: "white",
                        fontWeight: "700",
                        fontSize: "0.9rem",
                        margin: 0,
                        letterSpacing: "0.01em",
                      }}
                    >
                      {section.title}
                    </h4>
                  </div>
                  <div
                    style={{
                      padding: "1.25rem",
                      backgroundColor: section.bodyBg,
                    }}
                  >
                    {section.items.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "0.75rem",
                          marginBottom:
                            index === section.items.length - 1 ? 0 : "0.75rem",
                        }}
                      >
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "22px",
                            height: "22px",
                            borderRadius: "50%",
                            backgroundColor: section.numberColor,
                            color: "white",
                            fontSize: "0.65rem",
                            fontWeight: "800",
                            flexShrink: 0,
                            marginTop: "1px",
                          }}
                        >
                          {index + 1}
                        </span>
                        <span
                          style={{
                            fontSize: "0.875rem",
                            lineHeight: "1.6",
                            color: "#374151",
                          }}
                        >
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Good News Callout */}
            <div
              style={{
                background: "linear-gradient(135deg, #fefce8, #fef9e7)",
                border: "1px solid #e4d6a7",
                borderRadius: "12px",
                padding: "1.5rem",
                display: "flex",
                alignItems: "flex-start",
                gap: "1rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #c9a750, #e4c070)",
                  color: "white",
                  flexShrink: 0,
                }}
              >
                <EmojiObjects style={{ fontSize: "1.25rem" }} />
              </div>
              <div>
                <p
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: "800",
                    color: "#b8923d",
                    textTransform: "uppercase",
                    letterSpacing: "0.07em",
                    marginBottom: "0.375rem",
                    margin: "0 0 0.375rem 0",
                  }}
                >
                  The Good News
                </p>
                <p
                  style={{
                    fontSize: "1rem",
                    lineHeight: "1.7",
                    color: "#2d3748",
                    fontWeight: "500",
                    margin: 0,
                  }}
                >
                  {obstacleDetails.goodNews}
                </p>
              </div>
            </div>

            {/* Next Step Text */}
            {obstacleDetails.nextStep && (
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #152A53 0%, #1e3a6e 100%)",
                  borderRadius: "20px",
                  padding: isMobile ? "2rem" : "2.5rem",
                  position: "relative",
                  overflow: "hidden",
                  marginTop: "1.5rem",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "-60px",
                    right: "-60px",
                    width: "200px",
                    height: "200px",
                    borderRadius: "50%",
                    background: "rgba(201,167,80,0.12)",
                    pointerEvents: "none",
                  }}
                />
                <h3
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: "800",
                    color: "white",
                    margin: "0 0 1.25rem 0",
                    letterSpacing: "0.01em",
                  }}
                >
                  Your Next Step
                </h3>
                <p
                  style={{
                    fontSize: "1.05rem",
                    lineHeight: "1.7",
                    color: "rgba(255,255,255,0.9)",
                    fontWeight: "500",
                    marginBottom: "0.875rem",
                  }}
                >
                  {obstacleDetails.nextStep}
                </p>
                <p
                  style={{
                    fontSize: "0.9rem",
                    lineHeight: "1.7",
                    color: "rgba(255,255,255,0.65)",
                    margin: 0,
                  }}
                >
                  {obstacleDetails.closingStatement}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiagnosticResults;
